import { useEffect, useState } from 'react';
import { List } from 'antd';

import { useLocalData } from '../../../../../hooks/useLocalData';
import { useNotificationContext } from '../../../../../contexts/NotificationContext';
import { useTranslation } from 'react-i18next';

import ConfirmModelComponent from '../../../../../components/model/ConfirmationModelComponent';
import { deleteRecurrentTasks, getRecurrentTasks } from '../../../../../api/services/taskService';
import { referenceDataSyncService } from '../../../../../services/referenceDataSyncService';

import RecurrentTask from './RecurrentTask';
import { PermissionEnum } from '../../../../../enums/PermissionEnum';
import { usePermissions } from '../../../../../hooks/usePermissions';

interface TaskListsProps {
  refreshTaskSection?: number;
}

const TaskLists: React.FC<TaskListsProps> = ({ refreshTaskSection }) => {
  const { t } = useTranslation('task');
  const { data: tasks } = useLocalData({
    entity: 'task',
    autoSync: false,
    sortBy: 'name',
    sortOrder: 'asc',
  });
  const [taskData, setTaskData] = useState<any>(tasks);
  const [isLoadingTasks, setIsLoadingTasks] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalTasks, setTotalTasks] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(5);

  const { successNotification, errorNotification } = useNotificationContext();
  const { hasPermission } = usePermissions();

  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState<boolean>(false);
  const [selectedTaskId, setSelectedTaskId] = useState<number>();
  const [deleteMessage, setDeleteMessage] = useState<string | null>(null);
  const [noneToDelete, setNoneToDelete] = useState<boolean>(false);

  useEffect(() => {
    getAllTaskList();
  }, []);

  useEffect(() => {
    if (refreshTaskSection !== 0) {
      getAllTaskList();
    }
  }, [refreshTaskSection]);

  const getAllTaskList = async (page: number = currentPage, limit: number = pageSize) => {
    setIsLoadingTasks(true);
    try {
      const paginationParams = {
        page,
        limit,
      };

      const response = await getRecurrentTasks(paginationParams);

      if (response?.statusText === 'SUCCESS' && response?.data?.data) {
        const tasks = response?.data?.data;
        const total = response?.data?.total || 0;

        setTaskData(tasks);
        setTotalTasks(total);
        setCurrentPage(page);

        try {
          await referenceDataSyncService.forceSyncEntity('tasks');
        } catch (syncError) {
          console.warn('Failed to sync tasks using existing mechanism:', syncError);
        }
      } else {
        console.error('Failed to fetch tasks from backend:', response);
      }
    } catch (error) {
      console.error('Error fetching tasks from backend:', error);
    } finally {
      setIsLoadingTasks(false);
    }
  };

  useEffect(() => {
    getAllTaskList();
  }, []);

  const getDeleteConfirmation = (id: number, pendingTasks: number, totalTasks: number) => {
    if (!hasPermission(PermissionEnum.DELETE_INSPECTIONS)) return;

    setIsDeleteConfirmationOpen(true);
    setSelectedTaskId(id);
    setNoneToDelete(false);
    if (pendingTasks === totalTasks) {
      setDeleteMessage('deleteAllTasks');
    } else if (pendingTasks != 0) {
      setDeleteMessage('deleteOnlyPedingTasks');
    } else {
      setDeleteMessage('deleteNoPendingTasks');
      setNoneToDelete(true);
    }
  };

  const handleDelete = async () => {
    if (!hasPermission(PermissionEnum.DELETE_INSPECTIONS)) return;

    try {
      const selectedTask = taskData.find((task: any) => task?.id === selectedTaskId);

      if (selectedTask) {
        await deleteRecurrentTasks(selectedTaskId as number);
      }

      setIsDeleteConfirmationOpen(false);
      successNotification(t('taskDeleteSuccess'));

      getAllTaskList();
    } catch (error) {
      console.error('Failed to delete task:', error);
      setIsDeleteConfirmationOpen(false);
      errorNotification(t('taskDeleteFail'));
    }
  };

  const cancelDeleteConfirmation = () => {
    setNoneToDelete(false);
    setIsDeleteConfirmationOpen(false);
  };

  const handlePageChange = (page: number, newPageSize?: number) => {
    setCurrentPage(page);
    if (newPageSize && newPageSize !== pageSize) {
      setPageSize(newPageSize);
      getAllTaskList(1, newPageSize); // Reset to page 1 when page size changes
    } else {
      getAllTaskList(page);
    }
  };

  return (
    <div className="responsive-list-padding">
      <ConfirmModelComponent
        isOpen={isDeleteConfirmationOpen}
        onClickedCancel={cancelDeleteConfirmation}
        header={t('confirmDeleteTitle')}
        content={<p>{t(deleteMessage as string)}</p>}
        footerAction={
          noneToDelete
            ? {
                confimation: {
                  label: t('cancel'),
                  onClick: cancelDeleteConfirmation,
                },
              }
            : {
                confimation: {
                  label: t('confirm'),
                  onClick: handleDelete,
                },
                cancel: {
                  label: t('cancel'),
                  onClick: cancelDeleteConfirmation,
                },
              }
        }
        styleClasses="z-100"
      />

      <List
        pagination={{
          position: 'bottom',
          align: 'start',
          pageSize: pageSize,
          current: currentPage,
          total: totalTasks,
          showSizeChanger: true,
          showQuickJumper: true,
          pageSizeOptions: ['2', '5', '10'],
          showTotal: (total, range) => `${range[0]}-${range[1]} ${t('of')} ${total} ${t('tasks')}`,
          onChange: handlePageChange,
          onShowSizeChange: handlePageChange,
          locale: {
            jump_to: t('goTo'),
            page: t('page'),
            items_per_page: `/ ${t('page')}`,
          },
          className: 'mx-3 md:mx-4',
        }}
        split={false}
        loading={isLoadingTasks}
        dataSource={taskData}
        renderItem={(item: any, index) => (
          <List.Item key={item?.id + index}>
            <RecurrentTask item={item} getDeleteConfirmation={getDeleteConfirmation} />
          </List.Item>
        )}
      />
    </div>
  );
};

export default TaskLists;
